import { UIElement } from '../ui-element';
import { isPlainObject } from '../../global/helpers';
import { setInnerText } from '../../global/ui-helpers';
import type { UISurfaceColor } from '../../types/color.type';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import {
    UICalculatorLayout,
    UICalculatorLayouts,
    CalculationFunction,
    CallbackFunction,
    CalculationValue,
} from './ui-calculator.types';
import { Deprecated } from '../../decorators/deprecated.decorator';
import styles from './ui-calculator.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UICalculator
 * @element ui-calculator
 * @classdesc Represents a class for <code>ui-calculator</code> element.
 * @property {string} [label] {@attr label} Heading text.
 * @property {string} [labelDisclaimer] {@attr label-disclaimer} Disclaimer text,
 * shows at the bottom of calculator.
 * @property {UICalculatorLayouts} [layout="columns"] {@attr layout} Layout of calculator.
 *  {@desc columns: layout with 2 columns}
 *  {@desc fullwidth: layout will full width}
 * @property {Function} calculationFn - calculation middleware.
 * @property {Function} calculationCallback - callback when calculation is done.
 * @property {UISurfaceColor} [background] {@attr background} Sets background surface color for the calculator.
 *  {@desc neutral: Neutral color - Default}
 *  {@desc outlined: Outlined color}
 * Only for fff-ish theme
 * @slot
 * @example
 * <ui-calculator label="A calculator">
 *   <ui-columns>
 *     <ui-column>
 *       Calculation markup
 *       <ui-columns>
 *         <ui-column>
 *           Up to
 *         </ui-column>
 *         <ui-column>
 *           3
 *         </ui-column>
 *         <ui-column>
 *           columns
 *         </ui-column>
 *       </ui-columns>
 *     </ui-column>
 *     <ui-column>
 *       Result markup
 *     </ui-column>
 *   </ui-columns>
 * </ui-calculator>
 */
@Component({
    tag: 'ui-calculator',
    styles,
})
export class UICalculator extends UIElement {
    static readonly Layout: UICalculatorLayouts = {
        Columns: 'columns',
        Fullwidth: 'fullwidth',
    };

    @Deprecated('headings should not be inside of calculator')
    @Attribute(String)
    label?: string;
    @Attribute(String) labelDisclaimer?: string;
    @Attribute(String) layout?: UICalculatorLayout;
    @Attribute(Object) calculationFn?: CalculationFunction;
    @Attribute(Object) calculationCallback?: CallbackFunction;
    @Attribute(String) background?: UISurfaceColor;

    private _result?: Record<string, CalculationValue>;
    private readonly calculateHandler = this.calculate.bind(this);

    /**
     * Result accessor.
     * @returns {object}
     */
    getResult() {
        return this._result;
    }

    /**
     * Set calculation middleware for calculator. Also can be added a callback
     * when calculation is success.
     * @param {CalculationFunction} fn Middleware function should return the object with
     * mappings.
     * <code-preview language="json">
     * {
     *   propName1: value,
     *   propName2: value,
     *   ...
     *   propNameN: value
     * }
     * </code-preview>
     * Output will be automatically mapped to DOM elements with ID-s
     * in following format: #propName1-result, #propName2-result.
     * @param {CallbackFunction} cb Callback then calculation is finished.
     */
    setCalculation(fn: CalculationFunction, cb: CallbackFunction) {
        if (typeof fn === 'function') {
            this.calculationFn = fn;
        }
        if (typeof cb === 'function') {
            this.calculationCallback = cb;
        }
    }

    /**
     * Runs the calculations.
     */
    calculate() {
        if (typeof this.calculationFn === 'function') {
            this._result = this.calculationFn();
            this.applyMappings(this._result);
        }
        if (typeof this.calculationCallback === 'function') {
            this.calculationCallback();
        }
    }

    /**
     * Apply mappings to map return object with HTMLElement IDs. Returns
     * how many mappings were applied.
     * @param {object} obj
     * @returns {number}
     * @private
     */
    applyMappings(obj: Record<string, CalculationValue>) {
        let mappingsCount = 0;
        if (isPlainObject(obj)) {
            for (const key in obj) {
                /* istanbul ignore if */
                if (obj.hasOwnProperty(key)) {
                    const resultElem = document.getElementById(key + '-result');
                    if (resultElem) {
                        setInnerText(resultElem, String(obj[key]));
                        ++mappingsCount;
                    }
                }
            }
        }
        return mappingsCount;
    }

    /** @private */
    private getCalculableInputs(): NodeListOf<Element> {
        return this.querySelectorAll(
            'input, select, textarea, .ui-slider__input'
        );
    }

    /** @private */
    private subscribeEvents() {
        this.getCalculableInputs().forEach((input) => {
            input.addEventListener('change', this.calculateHandler);
        });
    }

    /** @private */
    private unsubscribeEvents() {
        this.getCalculableInputs().forEach((input) => {
            input.removeEventListener('change', this.calculateHandler);
        });
    }

    /**
     * @inheritDoc
     */
    render() {
        if (this.label) {
            const title = this.createElement({
                tagName: 'h2',
                children: [this.label],
            });
            this.insertAdjacentElement('afterbegin', title);
        }

        if (this.layout !== UICalculator.Layout.Fullwidth) {
            const columns = this.querySelector(
                'ui-columns:not(ui-columns ui-columns)'
            );
            columns?.setAttribute('bordered', '');
        }

        const contentWrapper = this.createElement({
            tagName: 'div',
            attributes: {
                class: 'calculator-content',
            },
            children: [...this.childNodes],
        });
        this.insertElements([contentWrapper]);

        if (this.labelDisclaimer) {
            const disclaimer = this.createElement({
                tagName: 'p',
                children: [this.labelDisclaimer],
                attributes: {
                    class: 'disclaimer',
                },
            });
            this.insertElements([disclaimer]);
        }
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        this.subscribeEvents();
    }
}
